import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import {
  Download,
  FileSpreadsheet,
  Lock,
  ShieldCheck,
  Loader2,
  CalendarRange,
  EyeOff,
  ListChecks,
  AlertTriangle,
} from 'lucide-react';
import { filterEligiblesView, type EligiblesView, type EligiblesFilters } from '@mdu-invoicing/shared';
import { useAuth } from '@/hooks/useAuth';
import { apiDownload, ApiError } from '@/lib/api-client';
import { eligiblesQueryString } from '@/lib/eligibles-query';
import { Modal } from '@/components/ui/Modal';

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' for display. */
function frDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

const FILTER_LABELS: Record<keyof EligiblesFilters, string> = {
  contractor: 'Contractant',
  area: 'Area Name',
  ssvDone: 'SSV Done',
  tsaDone: 'TSA Done',
  ssvValidation: 'Statut validation SSV (PBI)',
  tsaValidation: 'Statut validation TSA (PBI)',
  ssvDate: 'Date SSV',
  tsaDate: 'Date TSA',
  submission: 'Soumission',
  importFile: 'Fichier importé',
};

/**
 * Display-only filters that the EXPORT cannot honour: both read the working-set
 * overlay (`row.submission` / `row.decision.importBatchId`), which the server-side
 * export view does NOT carry — sending them would filter every row out and yield an
 * empty file. They are therefore stripped from the request AND from the recap, so
 * the announced counts always match the generated file (a filter is never silently
 * half-applied). Since Lot 6 pins `importFile` right after an import, this is a live
 * path, not a theoretical one.
 */
const EXPORT_IGNORED_FILTERS = ['submission', 'importFile'] as const satisfies readonly (keyof EligiblesFilters)[];

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  month: string;
  filters: EligiblesFilters;
  /** The already-loaded (raw) eligibles view from the screen — the recap is computed
   *  from it (no re-fetch); null while the grid is still loading. */
  view: EligiblesView | null;
}

/**
 * Export-for-partner-validation as a modal launched from Éligibles. Carries the
 * screen's current month + filters so the file matches exactly what is shown (the
 * server applies the same shared filter predicate). Amounts are never exported.
 */
export function ExportModal({ open, onClose, month, filters, view }: ExportModalProps) {
  const { isProvider, user } = useAuth();
  const [downloading, setDownloading] = useState(false);

  // What the server will actually apply (see EXPORT_IGNORED_FILTERS).
  const exportFilters = useMemo(() => {
    const f: EligiblesFilters = { ...filters };
    for (const k of EXPORT_IGNORED_FILTERS) delete f[k];
    return f;
  }, [filters]);
  const ignoredActive = EXPORT_IGNORED_FILTERS.filter(
    (k) => typeof filters[k] === 'string' && filters[k]!.trim() !== '',
  );

  const filtered = useMemo(
    () => (view ? filterEligiblesView(view, exportFilters) : null),
    [view, exportFilters],
  );

  const activeFilters = (Object.keys(FILTER_LABELS) as (keyof EligiblesFilters)[])
    .map((k) => ({ key: k, label: FILTER_LABELS[k], value: exportFilters[k] }))
    .filter((f): f is { key: keyof EligiblesFilters; label: string; value: string } =>
      typeof f.value === 'string' && f.value.trim() !== '',
    );

  async function handleDownload() {
    setDownloading(true);
    try {
      // Carry the same month + filters so the server builds an identical file.
      const { blob, filename } = await apiDownload(`/export?${eligiblesQueryString(month, exportFilters)}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Fichier d’export généré');
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Échec de la génération du fichier');
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Export pour validation partenaire">
      <div className="space-y-5">
        <p className="text-sm text-slate-500">
          Génère le fichier Excel <span className="font-mono text-xs">EXPORT_FOR_PARTNER_VALIDATION_…xlsx</span> que le
          partenaire complète hors-ligne (décision OK / REMOVE / ADD), puis ré-importe.
        </p>

        {/* Recap of what will be exported (current month + filters) */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <CalendarRange className="h-4 w-4 text-brand-purple" />
            Contenu de l'export
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Metric
              label="Période"
              value={filtered ? `${frDate(filtered.period.from)} → ${frDate(filtered.period.to)}` : '…'}
            />
            <Metric label="Éligibles SSV" value={filtered ? String(filtered.summary.ssvEligible) : '…'} />
            <Metric label="Éligibles TSA" value={filtered ? String(filtered.summary.tsaEligible) : '…'} />
            <Metric
              label="Périmètre"
              value={isProvider ? (user?.contractorName ?? '—') : 'Tous les contractants'}
            />
          </div>
          {activeFilters.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <span className="font-semibold uppercase tracking-wide text-slate-400">Filtres appliqués</span>
              <ul role="list" className="flex flex-wrap items-center gap-2">
                {activeFilters.map((f) => (
                  <li
                    key={f.key}
                    className="inline-flex items-center gap-1 rounded-full border border-brand-purple/20 bg-brand-purple/5 px-2 py-0.5 font-medium text-brand-purple"
                  >
                    {f.label} : {f.key === 'ssvDate' || f.key === 'tsaDate' ? frDate(f.value) : f.value}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {ignoredActive.length > 0 && (
            <p className="mt-3 text-xs text-amber-700">
              {ignoredActive.map((k) => FILTER_LABELS[k]).join(' / ')} : filtre d'affichage uniquement — non appliqué
              au fichier exporté (les chiffres ci-dessus en tiennent compte).
            </p>
          )}
        </div>

        {/* What the file contains / protection rules */}
        <div className="space-y-3 text-sm text-slate-600">
          <Feature icon={FileSpreadsheet} title="Trois onglets">
            <span className="font-medium text-slate-700">Eligible SSV</span>,{' '}
            <span className="font-medium text-slate-700">Eligible TSA</span> et un onglet{' '}
            <span className="font-medium text-slate-700">Filters</span>.
          </Feature>
          <Feature icon={Lock} title="Colonnes protégées">
            Seules « Eligible for invoicing » et « Eligibility comment » sont modifiables ; les colonnes internes
            Unifiber ne sont pas exportées.
          </Feature>
          <Feature icon={ListChecks} title="Menu + ajout de lignes">
            La colonne décision propose un menu OK / REMOVE / ADD ; des lignes vides déverrouillées permettent
            d'ajouter des cas manqués.
          </Feature>
          <Feature icon={AlertTriangle} title="Commentaire obligatoire sur REMOVE / ADD">
            Une ligne passée en <span className="font-medium text-slate-700">REMOVE</span> ou{' '}
            <span className="font-medium text-slate-700">ADD</span> doit porter un commentaire — sinon l'import du
            fichier est <span className="font-medium text-slate-700">intégralement rejeté</span>. La règle est aussi
            rappelée dans le fichier, sur la colonne décision.
          </Feature>
          <Feature icon={EyeOff} title="Montants non exportés">
            Le fichier ne contient aucun prix.
          </Feature>
          <Feature icon={ShieldCheck} title="Traçabilité">
            L'onglet Filters fige le contexte (utilisateur, horodatage, filtres).
          </Feature>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center gap-2 rounded-lg bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {downloading ? 'Génération…' : 'Télécharger le fichier'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-bold text-slate-800">{value}</div>
    </div>
  );
}

function Feature({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof FileSpreadsheet;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-purple/10 text-brand-purple">
        <Icon className="h-4 w-4" />
      </span>
      <p>
        <span className="font-semibold text-slate-800">{title}.</span> {children}
      </p>
    </div>
  );
}
